/**
 * Hooks for Database Viewer feature using TanStack Query for caching
 */

import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchModels, fetchModelData, reloadModels } from '@/lib/database-api'
import type { ModelInfo, ModelDataResponse, ModelDataOptions } from '@/types/database'

// Query key factories for consistent key structure
export const databaseKeys = {
  all: ['database'] as const,
  models: (projectId: number) => [...databaseKeys.all, 'models', projectId] as const,
  modelData: (projectId: number, appLabel: string, modelName: string, options: ModelDataOptions) =>
    [...databaseKeys.all, 'data', projectId, appLabel, modelName, options] as const,
}

interface UseModelsResult {
  data: { models: ModelInfo[]; total_count: number } | undefined
  isLoading: boolean
  error: Error | null
  isReloading: boolean
  reload: () => void
}

/**
 * Hook to fetch all models for a project with caching.
 * - queryFn uses GET (fast path from existing worker)
 * - reload() restarts the worker then updates cache with fresh data
 */
export function useModels(projectId: number, enabled = true): UseModelsResult {
  const queryClient = useQueryClient()
  const [isReloading, setIsReloading] = useState(false)

  const query = useQuery({
    queryKey: databaseKeys.models(projectId),
    queryFn: () => fetchModels(projectId),
    enabled,
    staleTime: 5 * 60 * 1000, // Models list cached for 5 minutes
  })

  const reload = useCallback(async () => {
    setIsReloading(true)
    try {
      const freshData = await reloadModels(projectId)
      queryClient.setQueryData(databaseKeys.models(projectId), freshData)
    } catch (e) {
      console.warn('[Database] Reload failed, falling back to refetch:', e)
      await query.refetch()
    } finally {
      setIsReloading(false)
    }
  }, [projectId, queryClient, query])

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isReloading,
    reload,
  }
}

interface UseModelDataResult {
  data: ModelDataResponse | undefined
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Hook to fetch model data with pagination and caching
 * - Caches each page/sort/search combination separately
 * - Shows cached data instantly when switching between models
 * - Deduplicates simultaneous requests automatically
 */
export function useModelData(
  projectId: number,
  appLabel: string | null,
  modelName: string | null,
  options: ModelDataOptions = {}
): UseModelDataResult {
  const enabled = !!appLabel && !!modelName

  const query = useQuery({
    queryKey: enabled
      ? databaseKeys.modelData(projectId, appLabel!, modelName!, options)
      : ['disabled'],
    queryFn: () => fetchModelData(projectId, appLabel!, modelName!, options),
    enabled,
    staleTime: 2 * 60 * 1000, // Data cached for 2 minutes (shorter than models)
    placeholderData: (previousData) => previousData, // Keep showing old data while fetching
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  }
}
